/**
 * Client Resource Configuration Tab
 * Manages ClientResourceMap entries with order editing using TanStack Table v8
 * TODO: Add drag-and-drop reordering in future update
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Chip,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Menu,
} from '@mui/material';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { API, graphqlOperation } from 'aws-amplify';
import AdminButton from 'components/common/AdminButton';
import ClientSelector from 'components/common/admin/ClientSelector';
import { useUser } from 'hooks/useUser';
import { useAdminContext } from 'context/AdminContext';
import { useClientResourceMap, useResourceCatalog } from '../hooks';

/**
 * Client Resource Configuration Tab Component
 * Displays and manages client-specific resource configurations
 */
function ClientResourceConfigTab() {
  const { user } = useUser();
  const {
    selectedClient,
    setSelectedClient,
    isAppAdmin,
    availableClients,
    loading: adminLoading,
    error: adminError
  } = useAdminContext();

  const clientId = selectedClient;

  const { resourceMaps, isLoading, error, create, update, remove, toggleEnabled, updateCustomization, isUpdating, isDeleting } = useClientResourceMap(clientId);
  const { catalogEntries } = useResourceCatalog();

  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newRowData, setNewRowData] = useState({
    clientId,
    resourceId: '',
    isEnabled: true,
    customLabel: '',
    customTooltip: '',
    customDescription: '',
    customIcon: '',
    order: 1,
  });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  // Update newRowData clientId when selected client changes
  useEffect(() => {
    setNewRowData(prev => ({ ...prev, clientId }));
  }, [clientId]);

  // Get available resources that aren't already mapped
  const availableResources = useMemo(() => {
    const mappedResourceIds = resourceMaps.map((rm) => rm.resourceId);
    return catalogEntries.filter((ce) => !mappedResourceIds.includes(ce.id));
  }, [resourceMaps, catalogEntries]);

  // Handle edit start
  const handleEdit = (row) => {
    setEditingId(row.id);
    setEditData({ ...row });
  };

  // Handle edit save (CLIENT_ADMIN safe - only updates customization fields)
  const handleSave = async () => {
    try {
      await updateCustomization(editingId, {
        customLabel: editData.customLabel,
        customTooltip: editData.customTooltip,
        customDescription: editData.customDescription,
        customIcon: editData.customIcon,
        order: editData.order,
      });
      setEditingId(null);
      setEditData({});
    } catch (err) {
      console.error('Failed to update client resource map:', err);
      alert(err.message || 'Failed to update resource configuration');
    }
  };

  // Handle edit cancel
  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to remove this resource from the client configuration?')) {
      try {
        await remove(id);
      } catch (err) {
        console.error('Failed to delete client resource map:', err);
      }
    }
  };

  // Handle toggle enabled
  const handleToggleEnabled = async (id, currentValue) => {
    try {
      await toggleEnabled(id, !currentValue);
    } catch (err) {
      console.error('Failed to toggle enabled status:', err);
    }
  };

  // Handle create new
  const handleCreateNew = () => {
    if (availableResources.length === 0) {
      alert('All available resources have been added to this client');
      return;
    }
    setIsCreatingNew(true);
    // Set order to next available value
    const maxOrder = resourceMaps.length > 0 ? Math.max(...resourceMaps.map((rm) => rm.order || 0)) : 0;
    setNewRowData({
      ...newRowData,
      order: maxOrder + 1,
    });
  };

  // Handle save new
  const handleSaveNew = async () => {
    if (!newRowData.resourceId) {
      alert('Please select a resource');
      return;
    }

    try {
      await create(newRowData);
      setIsCreatingNew(false);
      setNewRowData({
        clientId,
        resourceId: '',
        isEnabled: true,
        customLabel: '',
        customTooltip: '',
        customDescription: '',
        customIcon: '',
        order: 1,
      });
    } catch (err) {
      console.error('Failed to create client resource map:', err);
      alert(err.message || 'Failed to add resource to client');
    }
  };

  // Handle cancel new
  const handleCancelNew = () => {
    setIsCreatingNew(false);
    setNewRowData({
      clientId,
      resourceId: '',
      isEnabled: true,
      customLabel: '',
      customTooltip: '',
      customDescription: '',
      customIcon: '',
      order: 1,
    });
  };

  // Get resource catalog entry for a resource ID
  const getResourceCatalog = (resourceId) => {
    return catalogEntries.find((ce) => ce.id === resourceId);
  };

  // Handle menu open
  const handleMenuOpen = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setSelectedRow(row);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedRow(null);
  };

  // Handle menu edit
  const handleMenuEdit = () => {
    if (selectedRow) {
      handleEdit(selectedRow);
      handleMenuClose();
    }
  };

  // Handle menu delete
  const handleMenuDelete = () => {
    if (selectedRow) {
      handleDelete(selectedRow.id);
      handleMenuClose();
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: 'order',
        header: 'Order',
        cell: ({ row }) => {
          const isEditing = editingId === row.original.id;
          if (isEditing) {
            return (
              <TextField
                type="number"
                size="small"
                value={editData.order || 1}
                onChange={(e) => setEditData({ ...editData, order: parseInt(e.target.value, 10) })}
                inputProps={{ min: 1, max: 100 }}
                sx={{ width: '80px' }}
              />
            );
          }
          return row.original.order || '—';
        },
      },
      {
        accessorKey: 'resourceId',
        header: 'Resource',
        cell: ({ row }) => {
          const resource = getResourceCatalog(row.original.resourceId);
          return resource ? `${resource.resourceCode} - ${resource.name}` : row.original.resourceId;
        },
      },
      {
        accessorKey: 'isEnabled',
        header: 'Enabled',
        cell: ({ row }) => (
          <Switch
            checked={row.original.isEnabled}
            onChange={() => handleToggleEnabled(row.original.id, row.original.isEnabled)}
            size="small"
            disabled={editingId !== null || isCreatingNew}
          />
        ),
      },
      {
        accessorKey: 'customLabel',
        header: 'Custom Label',
        cell: ({ row }) => {
          const isEditing = editingId === row.original.id;
          if (isEditing) {
            return (
              <TextField
                size="small"
                value={editData.customLabel || ''}
                onChange={(e) => setEditData({ ...editData, customLabel: e.target.value })}
                fullWidth
                placeholder="Leave empty to use default"
              />
            );
          }
          return row.original.customLabel || '—';
        },
      },
      {
        accessorKey: 'customTooltip',
        header: 'Custom Tooltip',
        cell: ({ row }) => {
          const isEditing = editingId === row.original.id;
          if (isEditing) {
            return (
              <TextField
                size="small"
                value={editData.customTooltip || ''}
                onChange={(e) => setEditData({ ...editData, customTooltip: e.target.value })}
                fullWidth
                placeholder="Leave empty to use default"
              />
            );
          }
          return row.original.customTooltip || '—';
        },
      },
      {
        accessorKey: 'customIcon',
        header: 'Custom Icon',
        cell: ({ row }) => {
          const isEditing = editingId === row.original.id;
          if (isEditing) {
            return (
              <TextField
                size="small"
                value={editData.customIcon || ''}
                onChange={(e) => setEditData({ ...editData, customIcon: e.target.value })}
                fullWidth
                placeholder="Icon name"
              />
            );
          }
          return row.original.customIcon || '—';
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const isEditing = editingId === row.original.id;
          if (isEditing) {
            return (
              <Stack direction="row" spacing={1}>
                <Tooltip title="Save">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={handleSave}
                    disabled={isUpdating}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton
                    size="small"
                    onClick={handleCancel}
                    disabled={isUpdating}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            );
          }
          return (
            <Box display="flex" justifyContent="center">
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, row.original)}
                disabled={editingId !== null || isCreatingNew}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        },
      },
    ],
    [editingId, editData, isUpdating, isDeleting, isCreatingNew, catalogEntries]
  );

  // Table instance
  const table = useReactTable({
    data: resourceMaps,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      sorting: [{ id: 'order', desc: false }],
    },
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Failed to load client resource configuration: {error.message}
      </Alert>
    );
  }

  if (!clientId) {
    return (
      <Alert severity="warning" sx={{ mb: 3 }}>
        No client ID found. Please ensure you are logged in.
      </Alert>
    );
  }

  return (
    <Box>
      {/* White Background Container */}
      <Box sx={{
        backgroundColor: '#fff',
        borderRadius: 2,
        p: 3,
      }}>
        {/* APP_ADMIN Client Selector */}
        {isAppAdmin && (
          <Box mb={3} sx={{ maxWidth: 400 }}>
            <ClientSelector
              value={selectedClient}
              onChange={setSelectedClient}
              clients={availableClients}
              loading={adminLoading}
              error={adminError}
              disabled={isCreatingNew || editingId !== null}
            />
          </Box>
        )}

        {/* Header Section */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Client Resource Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure which resources are available for client: {clientId}
            </Typography>
          </Box>
          <AdminButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
            disabled={isCreatingNew || editingId !== null || availableResources.length === 0 || !clientId}
          >
            Add Resource to Client
          </AdminButton>
        </Box>

        {availableResources.length === 0 && !isCreatingNew && resourceMaps.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            All available resources have been added to this client.
          </Alert>
        )}

        {/* Table */}
        <Box sx={{
          overflowX: 'auto',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        borderBottom: '2px solid #e0e0e0',
                        backgroundColor: '#f5f5f5',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {/* New row form */}
              {isCreatingNew && (
                <tr style={{ backgroundColor: '#f0f7ff' }}>
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #e0e0e0', backgroundColor: '#fff' }}>
                    <TextField
                      type="number"
                      size="small"
                      value={newRowData.order}
                      onChange={(e) => setNewRowData({ ...newRowData, order: parseInt(e.target.value, 10) })}
                      inputProps={{ min: 1, max: 100 }}
                      sx={{ width: '80px' }}
                    />
                  </td>
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #e0e0e0', backgroundColor: '#fff' }}>
                    <FormControl size="small" fullWidth required>
                      <Select
                        value={newRowData.resourceId}
                        onChange={(e) => setNewRowData({ ...newRowData, resourceId: e.target.value })}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          Select Resource *
                        </MenuItem>
                        {availableResources.map((resource) => (
                          <MenuItem key={resource.id} value={resource.id}>
                            {resource.resourceCode} - {resource.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </td>
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #e0e0e0', backgroundColor: '#fff' }}>
                    <Switch
                      checked={newRowData.isEnabled}
                      onChange={(e) => setNewRowData({ ...newRowData, isEnabled: e.target.checked })}
                      size="small"
                    />
                  </td>
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #e0e0e0', backgroundColor: '#fff' }}>
                    <TextField
                      size="small"
                      value={newRowData.customLabel}
                      onChange={(e) => setNewRowData({ ...newRowData, customLabel: e.target.value })}
                      placeholder="Optional"
                      fullWidth
                    />
                  </td>
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #e0e0e0', backgroundColor: '#fff' }}>
                    <TextField
                      size="small"
                      value={newRowData.customTooltip}
                      onChange={(e) => setNewRowData({ ...newRowData, customTooltip: e.target.value })}
                      placeholder="Optional"
                      fullWidth
                    />
                  </td>
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #e0e0e0', backgroundColor: '#fff' }}>
                    <TextField
                      size="small"
                      value={newRowData.customIcon}
                      onChange={(e) => setNewRowData({ ...newRowData, customIcon: e.target.value })}
                      placeholder="Optional"
                      fullWidth
                    />
                  </td>
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #e0e0e0', backgroundColor: '#fff' }}>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Save">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={handleSaveNew}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton
                          size="small"
                          onClick={handleCancelNew}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </td>
                </tr>
              )}

              {/* Data rows */}
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid #e0e0e0',
                        backgroundColor: '#fff',
                        fontSize: '0.875rem',
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}

              {/* Empty state */}
              {table.getRowModel().rows.length === 0 && !isCreatingNew && (
                <tr>
                  <td
                    colSpan={columns.length}
                    style={{
                      padding: '48px 16px',
                      textAlign: 'center',
                      color: '#999',
                      backgroundColor: '#fff',
                    }}
                  >
                    No resources configured for this client. Click "Add Resource to Client" to begin.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>

        <Box mt={2}>
          <Typography variant="caption" color="text.secondary">
            Note: Drag-and-drop reordering will be added in a future update. For now, use the Order field to manually set display order.
          </Typography>
        </Box>

        {/* Row Actions Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleMenuEdit}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
          <MenuItem onClick={handleMenuDelete} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Remove
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
}

export default ClientResourceConfigTab;
